// /login and /signup: one step for new and returning users.
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui';
import { useAuth } from '../../context/useAuth.jsx';
import { AuthShell, FormMsg, EMAIL_RE, maskEmail, inputClass } from './AuthShell.jsx';

export async function startEmailSignIn(email) {
  // Content-Type: application/json is required by the CSRF guard; axios
  // sets it for object bodies.
  return axios.post('/auth/email/start', { email }, { withCredentials: true });
}

export function EmailSignInForm({ submitLabel, onSent, initialEmail = '' }) {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setMsg("That doesn't look like an email address. Please check it and try again.");
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      await startEmailSignIn(value);
      onSent(maskEmail(value));
    } catch (err) {
      setBusy(false);
      setMsg(err?.response?.status === 400
        ? "That doesn't look like an email address. Please check it and try again."
        : 'Something went wrong. Please try again in a moment.');
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      <label htmlFor="signin-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
      <input
        id="signin-email"
        type="email"
        name="email"
        autoComplete="email"
        autoFocus
        className={inputClass}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Button type="submit" variant="primary" size="lg" className="w-full mt-4" disabled={busy}>
        {submitLabel}
      </Button>
      <FormMsg error>{msg}</FormMsg>
    </form>
  );
}

export default function EmailSignIn() {
  const { isAuthenticated, activated, isCheckingAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isCheckingAuth && isAuthenticated) navigate(activated ? '/app' : '/welcome', { replace: true });
  }, [isCheckingAuth, isAuthenticated, activated, navigate]);

  return (
    <AuthShell
      aside={<>One account for <b>flowize-mcp.xyz</b> and <b>flowize.xyz</b>. Sign in on either.</>}
    >
      <h1 className="text-2xl font-semibold text-gray-900">Hi there. What's your email?</h1>
      <p className="text-sm text-gray-600 mt-2 mb-6">
        New or returning, it's the same step. We'll email you a link to sign in, so there's no password to remember.
      </p>
      <EmailSignInForm
        submitLabel="Email me a link"
        onSent={(masked) => navigate('/signin/check', { state: { masked } })}
      />
      <p className="text-xs text-gray-500 mt-6">
        By continuing you agree to our <Link className="underline" to="/terms">Terms</Link> and{' '}
        <Link className="underline" to="/privacy">Privacy policy</Link>.
      </p>
    </AuthShell>
  );
}
